import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Alert, AlertDescription } from "../components/ui/alert";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";
import { 
  Shield,
  Users,
  Building2,
  FileText,
  AlertTriangle,
  ClipboardCheck,
  MessageSquare,
  Calendar,
  CreditCard,
  Map,
  Megaphone,
  Activity,
  Settings,
  LogOut,
  Search,
  Plus,
  ChevronRight,
  UserPlus,
  KeyRound,
  Ban,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  RefreshCw,
  BarChart3,
  Eye,
  Edit,
  Trash2,
  Mountain,
  HelpCircle,
  FileCheck,
  Zap
} from "lucide-react";

const ROLES = [
  { value: "admin", label: "Admin", color: "bg-red-500/10 text-red-600" },
  { value: "property_manager", label: "Property Manager", color: "bg-indigo-500/10 text-indigo-600" },
  { value: "accountant", label: "Accountant", color: "bg-emerald-500/10 text-emerald-600" },
  { value: "board", label: "Board", color: "bg-purple-500/10 text-purple-600" },
  { value: "drc", label: "DRC", color: "bg-blue-500/10 text-blue-600" },
  { value: "owner", label: "Owner", color: "bg-green-500/10 text-green-600" },
  { value: "guest", label: "Guest", color: "bg-gray-500/10 text-gray-600" }
];

const STATUS_OPTIONS = [
  { value: "active", label: "Active", color: "bg-green-500/10 text-green-600" },
  { value: "inactive", label: "Inactive", color: "bg-gray-500/10 text-gray-600" },
  { value: "suspended", label: "Suspended", color: "bg-red-500/10 text-red-600" },
  { value: "pending", label: "Pending", color: "bg-yellow-500/10 text-yellow-600" }
];

const AdminDashboardPage = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  const [activeSection, setActiveSection] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  
  // Users state
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  
  // Create user state
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", name: "", role: "owner", password: "" });
  
  // Config state
  const [config, setConfig] = useState(null);
  
  // Audit logs state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditStats, setAuditStats] = useState(null);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/board");
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (userSearch) params.append("search", userSearch);
      if (roleFilter && roleFilter !== "all") params.append("role", roleFilter);
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      
      const response = await axios.get(`${API}/admin/users?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error("Failed to fetch config:", error);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/audit-logs?limit=50`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/audit-logs/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAuditLogs(logsRes.data.logs);
      setAuditTotal(logsRes.data.total);
      setAuditStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch audit logs:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchDashboard();
      setLoading(false);
    };
    loadData();
  }, [token]);

  useEffect(() => {
    if (activeSection === "users") fetchUsers();
    if (activeSection === "config") fetchConfig();
    if (activeSection === "audit") fetchAuditLogs();
  }, [activeSection, userSearch, roleFilter, statusFilter]);

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.name || !newUser.role) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/admin/users/create`, null, {
        params: newUser,
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User created! Temporary password: ${response.data.temporary_password}`);
      setCreateUserOpen(false);
      setNewUser({ email: "", name: "", role: "owner", password: "" });
      fetchUsers();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, null, {
        params: { new_role: newRole },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Role updated");
      fetchUsers();
      if (selectedUser?.user_id === userId) {
        setSelectedUser({ ...selectedUser, role: newRole });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleStatusChange = async (userId, newStatus, reason = "") => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, null, {
        params: { new_status: newStatus, reason },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Status updated");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handleResetPassword = async (userId) => {
    const newPassword = prompt("Enter new password (min 8 characters):");
    if (!newPassword || newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    try {
      await axios.post(`${API}/admin/users/${userId}/reset-password`, null, {
        params: { new_password: newPassword },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Password reset successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  const handleModuleToggle = async (moduleName, enabled) => {
    try {
      await axios.put(`${API}/admin/config/modules/${moduleName}`, null, {
        params: { enabled },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Module ${enabled ? "enabled" : "disabled"}`);
      fetchConfig();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update module");
    }
  };

  const handleExportAuditLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/audit-logs/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_logs_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Audit logs exported");
    } catch (error) {
      toast.error("Failed to export audit logs");
    }
  };

  const getRoleBadge = (role) => {
    const r = ROLES.find(r => r.value === role);
    return <Badge className={r?.color || "bg-gray-100"}>{r?.label || role}</Badge>;
  };

  const getStatusBadge = (status) => {
    const s = STATUS_OPTIONS.find(s => s.value === status);
    return <Badge className={s?.color || "bg-gray-100"}>{s?.label || status || "Active"}</Badge>;
  };

  const navItems = [
    { id: "dashboard", icon: <BarChart3 className="w-4 h-4" />, label: "Dashboard" },
    { id: "users", icon: <Users className="w-4 h-4" />, label: "Users" },
    { id: "config", icon: <Settings className="w-4 h-4" />, label: "System Config" },
    { id: "audit", icon: <Activity className="w-4 h-4" />, label: "Audit Logs" },
  ];

  const moduleLinks = [
    { href: "/board/documents", icon: <FileText className="w-4 h-4" />, label: "Documents", module: "documents" },
    { href: "/board/properties", icon: <Building2 className="w-4 h-4" />, label: "Properties", module: "properties" },
    { href: "/board/owners", icon: <Users className="w-4 h-4" />, label: "Owners", module: "owners" },
    { href: "/board/violations", icon: <AlertTriangle className="w-4 h-4" />, label: "Notices", module: "violations" },
    { href: "/board/drc", icon: <ClipboardCheck className="w-4 h-4" />, label: "DRC", module: "drc" },
    { href: "/board/correspondence", icon: <MessageSquare className="w-4 h-4" />, label: "Correspondence", module: "correspondence" },
    { href: "/board/meetings", icon: <Calendar className="w-4 h-4" />, label: "Meetings", module: "meetings" },
    { href: "/board/payments", icon: <CreditCard className="w-4 h-4" />, label: "Payments", module: "payments" },
    { href: "/board/forum", icon: <MessageSquare className="w-4 h-4" />, label: "Forum", module: "forum" },
    { href: "/board/gis", icon: <Map className="w-4 h-4" />, label: "GIS Map", module: "gis" },
    { href: "/board/announcements", icon: <Megaphone className="w-4 h-4" />, label: "Announcements", module: "announcements" },
    { href: "/board/listing", icon: <FileCheck className="w-4 h-4" />, label: "Listing Toolkit", module: "listing" },
    { href: "/board/automation", icon: <Zap className="w-4 h-4" />, label: "Automation", module: "automation" },
    { href: "/board/admin/ai-settings", icon: <Settings className="w-4 h-4" />, label: "AI Settings", module: null },
    { href: "/admin/ai-logs", icon: <Activity className="w-4 h-4" />, label: "AI Logs", module: null },
    { href: "/admin/backups", icon: <Download className="w-4 h-4" />, label: "Backups", module: null },
    { href: "/admin/site-settings", icon: <Settings className="w-4 h-4" />, label: "Site Settings", module: null },
    { href: "/board/gis/bulk-import", icon: <Map className="w-4 h-4" />, label: "Bulk GIS Import", module: null },
    { href: "/admin/knowledge-base", icon: <HelpCircle className="w-4 h-4" />, label: "Knowledge Base", module: null },
  ];

  const isModuleEnabled = (moduleName) => {
    if (!moduleName) return true;
    return dashboard?.modules?.[moduleName]?.enabled !== false;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading admin dashboard...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="w-64 bg-card border-r min-h-screen p-4 flex flex-col">
        <div className="flex items-center gap-3 mb-8 px-2">
          <div className="w-10 h-10 rounded-lg bg-red-600 flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="font-serif font-semibold">Admin Panel</p>
            <p className="text-xs text-muted-foreground">Horse Mountain Ranch</p>
          </div>
        </div>
        
        {/* Admin Navigation */}
        <div className="space-y-1 mb-6">
          <p className="text-xs font-semibold text-muted-foreground px-2 mb-2">ADMINISTRATION</p>
          {navItems.map((item) => (
            <Button
              key={item.id}
              variant={activeSection === item.id ? "secondary" : "ghost"}
              size="sm"
              className="w-full justify-start"
              onClick={() => setActiveSection(item.id)}
            >
              {item.icon}
              <span className="ml-2">{item.label}</span>
            </Button>
          ))}
        </div>
        
        {/* Module Links */}
        <div className="space-y-1 flex-1">
          <p className="text-xs font-semibold text-muted-foreground px-2 mb-2">MODULES</p>
          {moduleLinks.map((link) => {
            const enabled = isModuleEnabled(link.module);
            return (
              <Link key={link.href} to={link.href}>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`w-full justify-start ${!enabled ? 'opacity-50' : ''}`}
                  disabled={!enabled}
                >
                  {link.icon}
                  <span className="ml-2">{link.label}</span>
                  {!enabled && <Badge variant="outline" className="ml-auto text-xs">Off</Badge>}
                </Button>
              </Link>
            );
          })}
        </div>
        
        {/* User Info & Logout */}
        <div className="border-t pt-4 mt-4">
          <div className="px-2 mb-3">
            <p className="font-medium text-sm">{user?.name}</p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
            {getRoleBadge(user?.role)}
          </div>
          <Button variant="ghost" size="sm" className="w-full justify-start text-muted-foreground" onClick={logout}>
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        {/* Dashboard Section */}
        {activeSection === "dashboard" && dashboard && (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-serif font-bold">Admin Dashboard</h1>
              <p className="text-muted-foreground">System overview and quick actions</p>
            </div>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Users className="w-4 h-4" />
                    <span className="text-sm">Total Users</span>
                  </div>
                  <p className="text-2xl font-bold">{dashboard.users.total}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Building2 className="w-4 h-4" />
                    <span className="text-sm">Properties</span>
                  </div>
                  <p className="text-2xl font-bold">{dashboard.content.properties}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-yellow-600 mb-1">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm">Open Notices</span>
                  </div>
                  <p className="text-2xl font-bold text-yellow-600">{dashboard.content.open_violations}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-red-600 mb-1">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">Pending Claims</span>
                  </div>
                  <p className="text-2xl font-bold text-red-600">{dashboard.content.pending_claims}</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Users by Role */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Users by Role</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 gap-4">
                  {ROLES.map((role) => (
                    <div key={role.value} className="text-center p-3 bg-muted/50 rounded-lg">
                      <p className="text-2xl font-bold">{dashboard.users.by_role[role.value] || 0}</p>
                      {getRoleBadge(role.value)}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Quick Actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Button variant="outline" className="h-auto py-4 flex-col" onClick={() => setActiveSection("users")}>
                <UserPlus className="w-6 h-6 mb-2" />
                <span>Manage Users</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col" onClick={() => setActiveSection("config")}>
                <Settings className="w-6 h-6 mb-2" />
                <span>System Config</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col" onClick={() => setActiveSection("audit")}>
                <Activity className="w-6 h-6 mb-2" />
                <span>Audit Logs</span>
              </Button>
              <Link to="/board/owners">
                <Button variant="outline" className="h-auto py-4 flex-col w-full">
                  <Building2 className="w-6 h-6 mb-2" />
                  <span>Property Claims</span>
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Users Section */}
        {activeSection === "users" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-serif font-bold">User Management</h1>
                <p className="text-muted-foreground">Create, edit, and manage user accounts</p>
              </div>
              <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="create-user-btn">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Create User
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New User</DialogTitle>
                    <DialogDescription>Create a new user account with specified role</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                        placeholder="user@example.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input
                        value={newUser.name}
                        onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Role *</Label>
                      <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map(r => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Password (optional - will generate if empty)</Label>
                      <Input
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                        placeholder="Leave empty to auto-generate"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCreateUserOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateUser}>Create User</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {ROLES.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {STATUS_OPTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Users Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-4 font-medium">User</th>
                        <th className="text-left p-4 font-medium">Role</th>
                        <th className="text-left p-4 font-medium">Status</th>
                        <th className="text-left p-4 font-medium">Created</th>
                        <th className="text-left p-4 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.user_id} className="border-t hover:bg-muted/25">
                          <td className="p-4">
                            <div>
                              <p className="font-medium">{u.name}</p>
                              <p className="text-sm text-muted-foreground">{u.email}</p>
                            </div>
                          </td>
                          <td className="p-4">
                            <Select value={u.role} onValueChange={(v) => handleRoleChange(u.user_id, v)}>
                              <SelectTrigger className="w-[120px]">
                                {getRoleBadge(u.role)}
                              </SelectTrigger>
                              <SelectContent>
                                {ROLES.map(r => (
                                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-4">{getStatusBadge(u.status)}</td>
                          <td className="p-4 text-sm text-muted-foreground">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : "N/A"}
                          </td>
                          <td className="p-4">
                            <div className="flex gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleResetPassword(u.user_id)}
                                title="Reset Password"
                              >
                                <KeyRound className="w-4 h-4" />
                              </Button>
                              {u.status === "active" ? (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleStatusChange(u.user_id, "suspended")}
                                  title="Suspend User"
                                >
                                  <Ban className="w-4 h-4 text-red-600" />
                                </Button>
                              ) : (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleStatusChange(u.user_id, "active")}
                                  title="Activate User"
                                >
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* System Config Section */}
        {activeSection === "config" && config && (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-serif font-bold">System Configuration</h1>
              <p className="text-muted-foreground">Configure modules, categories, and settings</p>
            </div>
            
            {/* Module Toggles */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Module Management</CardTitle>
                <CardDescription>Enable or disable platform modules</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(config.modules || {}).map(([key, module]) => (
                    <div key={key} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{module.name}</p>
                        <p className="text-xs text-muted-foreground">{key}</p>
                      </div>
                      <Switch
                        checked={module.enabled}
                        onCheckedChange={(checked) => handleModuleToggle(key, checked)}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Notice Categories */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Notice Categories & Fines</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3">Category</th>
                        <th className="text-left p-3">First Fine</th>
                        <th className="text-left p-3">Subsequent</th>
                        <th className="text-left p-3">Max Fine</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(config.violation_categories || []).map((cat) => (
                        <tr key={cat.value} className="border-t">
                          <td className="p-3 font-medium">{cat.label}</td>
                          <td className="p-3">${cat.fine_first}</td>
                          <td className="p-3">${cat.fine_subsequent}</td>
                          <td className="p-3">${cat.fine_max}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            
            {/* DRC Project Types */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">DRC Project Types</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {(config.drc_project_types || []).map((type) => (
                    <Badge key={type.value} variant="outline">{type.label}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Payment Types */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Payment Types</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {(config.payment_types || []).map((type) => (
                    <Badge key={type.value} variant="outline">{type.label}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Audit Logs Section */}
        {activeSection === "audit" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-serif font-bold">Audit Logs</h1>
                <p className="text-muted-foreground">Track all system activity</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={fetchAuditLogs}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
                <Button variant="outline" onClick={handleExportAuditLogs}>
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
            
            {/* Stats */}
            {auditStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Total Logs</p>
                    <p className="text-2xl font-bold">{auditStats.total_logs}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Last 24 Hours</p>
                    <p className="text-2xl font-bold">{auditStats.last_24_hours}</p>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* Logs Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto max-h-[600px]">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="text-left p-3">Timestamp</th>
                        <th className="text-left p-3">Action</th>
                        <th className="text-left p-3">Entity</th>
                        <th className="text-left p-3">User</th>
                        <th className="text-left p-3">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.map((log, idx) => (
                        <tr key={idx} className="border-t hover:bg-muted/25">
                          <td className="p-3 text-muted-foreground whitespace-nowrap">
                            {log.created_at ? new Date(log.created_at).toLocaleString() : "N/A"}
                          </td>
                          <td className="p-3">
                            <Badge variant="outline">{log.action}</Badge>
                          </td>
                          <td className="p-3">
                            <span className="text-muted-foreground">{log.entity_type}:</span> {log.entity_id}
                          </td>
                          <td className="p-3">{log.user_email || log.user_id}</td>
                          <td className="p-3 text-muted-foreground max-w-[200px] truncate">
                            {log.details ? JSON.stringify(log.details).substring(0, 50) : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboardPage;
